// @ts-nocheck
import { apiClient } from './apiClient';

export interface AuditEntry {
  id: string;
  timestamp: string;
  action: string;
  actor: string;
  actorId: string;
  resource: string;
  resourceId?: string;
  details: string;
  ipAddress: string;
  userAgent?: string;
  compliance: string[];
  severity: 'low' | 'medium' | 'high' | 'critical';
  metadata?: Record<string, any>;
}

export interface ConsentRecord {
  id: string;
  userId: string;
  type: string;
  granted: boolean;
  timestamp: string;
  expiresAt?: string;
  purpose: string;
  dataCategories: string[];
  withdrawable: boolean;
  parentConsentRequired?: boolean;
  parentConsentId?: string;
  version: string;
}

export interface DataRequest {
  id: string;
  userId: string;
  type: 'access' | 'portability' | 'erasure' | 'rectification';
  status: 'pending' | 'processing' | 'completed' | 'rejected';
  requestedAt: string;
  completedAt?: string;
  downloadUrl?: string;
  notes?: string;
  legalBasis?: string;
  verificationRequired?: boolean;
  verificationMethod?: string;
  retentionExempt?: boolean;
}

export interface PrivacySettings {
  marketingEmails: boolean;
  dataSharing: boolean;
  analytics: boolean;
  personalizedContent: boolean;
  retentionPeriod: string;
  minorProtection: boolean;
  thirdPartySharing: boolean;
  crossBorderTransfer: boolean;
  automatedDecisionMaking: boolean;
  profiling: boolean;
}

export interface ComplianceStatus {
  gdpr: boolean;
  ccpa: boolean;
  coppa: boolean;
  ferpa: boolean;
  lastVerified: string;
  certifications: string[];
  violations: number;
  auditRequired: boolean;
}

export interface RetentionPolicy {
  id: string;
  dataType: string;
  retentionPeriod: string;
  purpose: string;
  legalBasis: string;
  autoDelete: boolean;
  reviewRequired: boolean;
  lastReviewed: string;
  nextReview: string;
}

class PrivacyService {
  private baseUrl = '/api/v1/privacy';

  // Audit Log Methods
  async getUserAuditLog(
    userId: string,
    options: {
      page?: number;
      pageSize?: number;
      filter?: string;
      startDate?: string;
      endDate?: string;
      severity?: string;
      compliance?: string[];
    } = {}
  ) {
    const params = new URLSearchParams();
    if (options.page) params.append('page', options.page.toString());
    if (options.pageSize) params.append('pageSize', options.pageSize.toString());
    if (options.filter) params.append('filter', options.filter);
    if (options.startDate) params.append('startDate', options.startDate);
    if (options.endDate) params.append('endDate', options.endDate);
    if (options.severity) params.append('severity', options.severity);
    if (options.compliance) params.append('compliance', options.compliance.join(','));

    const response = await apiClient.get(`${this.baseUrl}/audit/${userId}?${params.toString()}`);
    return response.data;
  }

  async exportAuditLog(userId: string, format: 'csv' | 'json' | 'pdf') {
    const response = await apiClient.get(`${this.baseUrl}/audit/${userId}/export?format=${format}`, {
      responseType: 'blob',
    });
    return response.data;
  }

  // Consent Management Methods
  async getUserConsents(userId: string): Promise<ConsentRecord[]> {
    const response = await apiClient.get(`${this.baseUrl}/consent/${userId}`);
    return response.data;
  }

  async updateConsent(userId: string, consentId: string, granted: boolean, metadata?: Record<string, any>) {
    const response = await apiClient.put(`${this.baseUrl}/consent/${userId}/${consentId}`, { granted, metadata });
    return response.data;
  }

  async recordConsent(userId: string, consent: Omit<ConsentRecord, 'id' | 'userId' | 'timestamp'>) {
    const response = await apiClient.post(`${this.baseUrl}/consent/${userId}`, consent);
    return response.data;
  }

  async withdrawConsent(userId: string, consentId: string, reason?: string) {
    const response = await apiClient.post(`${this.baseUrl}/consent/${userId}/${consentId}/withdraw`, { reason });
    return response.data;
  }

  async getConsentHistory(userId: string, consentType?: string) {
    const params = consentType ? `?type=${consentType}` : '';
    const response = await apiClient.get(`${this.baseUrl}/consent/${userId}/history${params}`);
    return response.data;
  }

  // Data Request Methods
  async getUserDataRequests(userId: string): Promise<DataRequest[]> {
    const response = await apiClient.get(`${this.baseUrl}/requests/${userId}`);
    return response.data;
  }

  async submitDataRequest(
    userId: string,
    request: {
      type: 'access' | 'portability' | 'erasure' | 'rectification';
      reason?: string;
      format?: string;
      categories?: string[];
    }
  ) {
    const response = await apiClient.post(`${this.baseUrl}/requests/${userId}`, request);
    return response.data;
  }

  async cancelDataRequest(userId: string, requestId: string) {
    const response = await apiClient.delete(`${this.baseUrl}/requests/${userId}/${requestId}`);
    return response.data;
  }

  async getDataRequestStatus(userId: string, requestId: string) {
    const response = await apiClient.get(`${this.baseUrl}/requests/${userId}/${requestId}/status`);
    return response.data;
  }

  async downloadDataExport(userId: string, requestId: string) {
    const response = await apiClient.get(`${this.baseUrl}/requests/${userId}/${requestId}/download`, {
      responseType: 'blob',
    });
    return response.data;
  }

  // Privacy Settings Methods
  async getUserPrivacySettings(userId: string): Promise<PrivacySettings> {
    const response = await apiClient.get(`${this.baseUrl}/settings/${userId}`);
    return response.data;
  }

  async updatePrivacySettings(userId: string, settings: Partial<PrivacySettings>) {
    const response = await apiClient.patch(`${this.baseUrl}/settings/${userId}`, settings);
    return response.data;
  }

  async resetPrivacySettings(userId: string) {
    const response = await apiClient.post(`${this.baseUrl}/settings/${userId}/reset`);
    return response.data;
  }

  // Compliance Methods
  async getComplianceStatus(userId: string): Promise<ComplianceStatus> {
    const response = await apiClient.get(`${this.baseUrl}/compliance/${userId}`);
    return response.data;
  }

  async requestComplianceAudit(userId: string) {
    const response = await apiClient.post(`${this.baseUrl}/compliance/${userId}/audit`);
    return response.data;
  }

  async getComplianceReport(userId: string, regulation: string) {
    const response = await apiClient.get(`${this.baseUrl}/compliance/${userId}/report?regulation=${regulation}`);
    return response.data;
  }

  // Retention Policy Methods
  async getRetentionPolicies(): Promise<RetentionPolicy[]> {
    const response = await apiClient.get(`${this.baseUrl}/retention`);
    return response.data;
  }

  async getUserRetentionStatus(userId: string) {
    const response = await apiClient.get(`${this.baseUrl}/retention/${userId}`);
    return response.data;
  }

  async requestRetentionExtension(userId: string, dataType: string, reason: string) {
    const response = await apiClient.post(`${this.baseUrl}/retention/${userId}/extend`, { dataType, reason });
    return response.data;
  }

  // COPPA/Parental Consent Methods
  async checkMinorStatus(userId: string) {
    const response = await apiClient.get(`${this.baseUrl}/coppa/${userId}/status`);
    return response.data;
  }

  async requestParentalConsent(minorId: string, parentEmail: string, consentTypes: string[]) {
    const response = await apiClient.post(`${this.baseUrl}/coppa/${minorId}/parent-consent`, {
      parentEmail,
      consentTypes,
    });
    return response.data;
  }

  async verifyParentalConsent(verificationToken: string, verificationMethod: 'email' | 'creditcard' | 'id') {
    const response = await apiClient.post(`${this.baseUrl}/coppa/verify`, { verificationToken, verificationMethod });
    return response.data;
  }

  async getParentalAccessToken(minorId: string, parentId: string) {
    const response = await apiClient.post(`${this.baseUrl}/coppa/${minorId}/parent-access`, { parentId });
    return response.data;
  }

  // FERPA Educational Records Methods
  async getEducationalRecordsAccess(studentId: string) {
    const response = await apiClient.get(`${this.baseUrl}/ferpa/${studentId}/access`);
    return response.data;
  }

  async requestEducationalRecordsReview(studentId: string) {
    const response = await apiClient.post(`${this.baseUrl}/ferpa/${studentId}/review`);
    return response.data;
  }

  async updateEducationalRecordsConsent(studentId: string, institutionId: string, consent: boolean) {
    const response = await apiClient.put(`${this.baseUrl}/ferpa/${studentId}/consent`, { institutionId, consent });
    return response.data;
  }

  async getDirectoryInformationSettings(studentId: string) {
    const response = await apiClient.get(`${this.baseUrl}/ferpa/${studentId}/directory`);
    return response.data;
  }

  async updateDirectoryInformationSettings(studentId: string, settings: Record<string, boolean>) {
    const response = await apiClient.put(`${this.baseUrl}/ferpa/${studentId}/directory`, settings);
    return response.data;
  }

  // Data Breach Notification Methods
  async getDataBreachNotifications(userId: string) {
    const response = await apiClient.get(`${this.baseUrl}/breach/${userId}`);
    return response.data;
  }

  async acknowledgeDataBreach(userId: string, breachId: string) {
    const response = await apiClient.post(`${this.baseUrl}/breach/${userId}/${breachId}/acknowledge`);
    return response.data;
  }

  // Third-party Data Sharing Methods
  async getThirdPartySharing(userId: string) {
    const response = await apiClient.get(`${this.baseUrl}/third-party/${userId}`);
    return response.data;
  }

  async updateThirdPartySharing(userId: string, thirdPartyId: string, allowed: boolean) {
    const response = await apiClient.put(`${this.baseUrl}/third-party/${userId}/${thirdPartyId}`, { allowed });
    return response.data;
  }

  async revokeThirdPartyAccess(userId: string, thirdPartyId: string) {
    const response = await apiClient.delete(`${this.baseUrl}/third-party/${userId}/${thirdPartyId}`);
    return response.data;
  }

  // Cookie Preferences Methods
  async getCookiePreferences(userId: string) {
    const response = await apiClient.get(`${this.baseUrl}/cookies/${userId}`);
    return response.data;
  }

  async updateCookiePreferences(
    userId: string,
    preferences: {
      necessary: boolean;
      functional: boolean;
      analytics: boolean;
      marketing: boolean;
    }
  ) {
    const response = await apiClient.put(`${this.baseUrl}/cookies/${userId}`, preferences);
    return response.data;
  }

  // Data Portability Methods
  async initiateDataPortability(userId: string, targetService: string, dataCategories: string[]) {
    const response = await apiClient.post(`${this.baseUrl}/portability/${userId}`, { targetService, dataCategories });
    return response.data;
  }

  async getPortabilityStatus(userId: string, transferId: string) {
    const response = await apiClient.get(`${this.baseUrl}/portability/${userId}/${transferId}`);
    return response.data;
  }
}

export const privacyService = new PrivacyService();
